import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "relative inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-opacity transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "font-semibold bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "font-semibold border border-slate-400 bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 font-bold",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-2 gap-1",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
      block: {
        false: null,
        true: ["w-full"],
      },
      loading: {
        true: "pointer-events-none text-transparent",
        false: null,
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      block,
      disabled,
      loading = false,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"

    // asChild の場合は Slot が単一の子要素のみ受け取るため、Spinner を含めない
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, block, loading, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, block, loading, className }))}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {children}
        <Spinner
          className={cn(
            "text-slate-300 absolute inset-0 m-auto size-5 transition-opacity",
            variant === "secondary" && "text-slate-700",
            !loading && "opacity-0 pointer-events-none"
          )}
        />
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
